import { useCallback, useContext } from 'react';
import { useRouter } from '@tanstack/react-router';

import { AppContext } from '@/app.context';
import { getPreviousHistoryEntrySearch } from '@shared/lib/history-entry-tracker';

import { useGoBack } from './use-go-back';

/**
 * How long the transition's update may wait for the page it is leaving to be
 * replaced before taking its "after" snapshot anyway. Generous on purpose:
 * the screen is frozen only while the next page is genuinely not drawn yet,
 * and the browser itself gives up on an update after a few seconds.
 */
const PAGE_SWAP_TIMEOUT_MS = 1000;

/**
 * Once the page being left is gone, how long to wait for the next one to draw
 * an element under the same `view-transition-name`. Short, since a page with
 * no counterpart (nothing to morph into) holds the frozen screen this long.
 */
const ARRIVAL_TIMEOUT_MS = 300;

/**
 * "Back" as a real history pop (`useGoBack`), wrapped in a view transition so
 * a cover-morph that brought this page in plays in reverse on the way out --
 * the issue reader's back (`useReaderGoBack`) and the reader-family preview's.
 *
 * `router.navigate()`/`<Link>` can't do a real pop -- `commitLocation` only
 * ever issues a `PUSH` or `REPLACE` -- so this owns the transition directly
 * rather than priming `router.shouldViewTransition` and hoping it survives
 * the async popstate gap (confirmed live not to).
 *
 * **The update resolves once the page being left is gone from the DOM**, not
 * on the router's `onResolved` alone. The router commits inside a React
 * transition, so when the page being returned to suspends (its lazy chunk, a
 * query) React keeps the old page on screen past `onResolved` -- and the
 * "after" snapshot was then the old page again: no element carried the
 * cover's `view-transition-name`, and the morph silently degraded to a
 * cross-fade (seen going from the reader back to starmagazine's preview).
 * "Gone" is read off the elements that carry an inline
 * `view-transition-name` (every cover-morph sets it as a style) -- once all of
 * them are disconnected, React has committed the next page -- and the update
 * then waits, briefly (`ARRIVAL_TIMEOUT_MS`), for that page to draw an
 * element under one of the same names. A page with none resolves on
 * `onResolved`, as before; `PAGE_SWAP_TIMEOUT_MS` bounds the whole wait.
 *
 * **Skipped when the entry being returned to has a `?dialog=` search
 * param** (a home reopening its Favorites/Downloads drawer). That page comes
 * back with a drawer covering it, so there's no cover to morph into, and
 * animating a snapshot of this page into it just looks broken. The previous
 * entry is read from `@shared/lib/history-entry-tracker`, since the History
 * API itself can't be asked what's behind the current entry.
 */
export function useTransitionGoBack(fallback?: string): () => void {
	const router = useRouter();
	const goBack = useGoBack(fallback);
	const { prefersReducedMotion } = useContext(AppContext);

	return useCallback(() => {
		const previousSearch = getPreviousHistoryEntrySearch(
			router.state.location,
		);
		if (
			prefersReducedMotion ||
			typeof document.startViewTransition !== 'function' ||
			!!previousSearch?.dialog
		) {
			goBack();
			return;
		}

		document.startViewTransition(
			() =>
				new Promise<void>((resolve) => {
					const leaving = Array.from(
						document.querySelectorAll<HTMLElement>(
							'[style*="view-transition-name"]',
						),
					);
					const names = new Set(
						leaving.map((el) => el.style.viewTransitionName),
					);
					const hasLeft = () => leaving.every((el) => !el.isConnected);
					// The next page draws an element under one of those names.
					const hasArrived = () =>
						Array.from(
							document.querySelectorAll<HTMLElement>(
								'[style*="view-transition-name"]',
							),
						).some((el) => names.has(el.style.viewTransitionName));
					let isResolved = false;
					let isDone = false;
					let arrivalTimeout: ReturnType<typeof setTimeout> | undefined;

					const finish = () => {
						if (isDone) return;
						isDone = true;
						observer?.disconnect();
						clearTimeout(timeout);
						clearTimeout(arrivalTimeout);
						unsubscribe();
						resolve();
					};
					const check = () => {
						if (!isResolved || !hasLeft()) return;
						if (!leaving.length || hasArrived()) {
							finish();
							return;
						}
						// Left, but nothing to morph into yet: the next page may
						// still be filling in (`SmoothSuspense` commits its content
						// a render after it mounts). Not every page has a
						// counterpart, so this wait is short.
						arrivalTimeout ??= setTimeout(finish, ARRIVAL_TIMEOUT_MS);
					};
					const observer = leaving.length
						? new MutationObserver(check)
						: null;
					observer?.observe(document.body, {
						childList: true,
						subtree: true,
						attributes: true,
						attributeFilter: ['style'],
					});
					const unsubscribe = router.subscribe('onResolved', () => {
						isResolved = true;
						check();
					});
					const timeout = setTimeout(finish, PAGE_SWAP_TIMEOUT_MS);

					goBack();
				}),
		);
	}, [router, goBack, prefersReducedMotion]);
}
